import { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { ContactPicker } from '@/features/contact-groups/ContactPicker';
import {
  useContactGroups,
  useContactGroup,
  useCreateContactGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useGroupTypes,
  useRelationshipTypes,
  type ContactGroupSummary,
  type GroupMember,
} from '@/features/contact-groups/hooks/useContactGroups';

/**
 * ContactGroupsTab — shown on the contact detail page. Lets the user see the
 * groups a contact belongs to, create a new group (auto-adding this contact),
 * and manage members (each added with a relationship type).
 *
 * This replaces the old standalone "Relationships" tab: a group with members
 * carrying relationship types now expresses the same relationships.
 */
export function ContactGroupsTab({ contactId }: { contactId: string }) {
  const { data, isLoading } = useContactGroups({ contact_id: contactId });
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { can } = usePermissions();

  return (
    <Card>
      <CardHeader
        title="Groups"
        description="Groups this contact belongs to. Add members with a relationship type to describe how they relate."
        action={
          can(PERMISSIONS.GROUPS_CREATE) ? (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              Create Group
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <p className="py-4 text-center text-sm text-gray-500">Loading...</p>
        ) : (data?.groups.length ?? 0) === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            This contact isn&apos;t in any group yet.
          </p>
        ) : (
          <ul role="list" className="divide-y divide-border dark:divide-border-dark">
            {data?.groups.map((g) => (
              <li key={g.id} className="py-3">
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {g.name}
                    </span>
                    {g.group_type && (
                      <Badge variant="info" size="sm">
                        {g.group_type}
                      </Badge>
                    )}
                  </span>
                  <Badge variant="default" size="sm">
                    {g.member_count} {g.member_count === 1 ? 'member' : 'members'}
                  </Badge>
                </button>
                {expandedId === g.id && <GroupMembersInline groupId={g.id} />}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {showCreate && (
        <CreateGroupForContactModal contactId={contactId} onClose={() => setShowCreate(false)} />
      )}
    </Card>
  );
}

// ─── Inline member list for an expanded group ───────────────────────────────

function GroupMembersInline({ groupId }: { groupId: string }) {
  const { data: group, isLoading } = useContactGroup(groupId);
  const { data: relTypes } = useRelationshipTypes();
  const [showAdd, setShowAdd] = useState(false);
  const [toRemove, setToRemove] = useState<GroupMember | null>(null);
  const removeMember = useRemoveGroupMember(groupId);
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.GROUPS_UPDATE);

  const relLabel = (code: string) => relTypes?.find((t) => t.code === code)?.label ?? code;

  if (isLoading || !group) {
    return <p className="mt-2 pl-2 text-xs text-gray-500">Loading members...</p>;
  }

  return (
    <div className="mt-2 rounded-lg bg-surface-secondary p-3 dark:bg-surface-dark-secondary">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Members
        </span>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
            Add Member
          </Button>
        )}
      </div>
      <ul className="divide-y divide-border dark:divide-border-dark">
        {group.members.map((m) => (
          <li key={m.id} className="flex items-center justify-between py-2">
            <span className="flex items-center gap-2">
              <span className="text-sm text-gray-900 dark:text-gray-100">{m.contact_name}</span>
              {m.is_primary && (
                <Badge variant="success" size="sm">
                  Billing
                </Badge>
              )}
              {m.relationship_type && (
                <Badge variant="info" size="sm">
                  {relLabel(m.relationship_type)}
                </Badge>
              )}
            </span>
            {canManage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setToRemove(m)}
                aria-label={`Remove ${m.contact_name}`}
              >
                Remove
              </Button>
            )}
          </li>
        ))}
      </ul>

      {showAdd && <AddMemberInlineModal groupId={groupId} onClose={() => setShowAdd(false)} />}

      <Dialog
        open={!!toRemove}
        onClose={() => setToRemove(null)}
        onConfirm={() => {
          if (!toRemove) return;
          removeMember.mutate(toRemove.contact_id, { onSuccess: () => setToRemove(null) });
        }}
        title="Remove Member"
        description={
          toRemove
            ? `Remove ${toRemove.contact_name} from this group? This does not delete the contact.`
            : ''
        }
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removeMember.isPending}
      />
    </div>
  );
}

function AddMemberInlineModal({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const [contactId, setContactId] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [error, setError] = useState('');
  const addMember = useAddGroupMember(groupId);
  const { data: relTypes } = useRelationshipTypes();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactId) {
      setError('Select a contact');
      return;
    }
    addMember.mutate(
      { contact_id: contactId, relationship_type: relationshipType || null },
      {
        onSuccess: onClose,
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message ?? 'Could not add member';
          setError(message);
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title="Add Member" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <ContactPicker label="Contact" value={contactId} onChange={setContactId} error={error} />
        <Select
          label="Relationship type (optional)"
          value={relationshipType}
          onChange={(e) => setRelationshipType(e.target.value)}
          placeholder="Select a relationship type"
          options={(relTypes ?? []).map((t) => ({ value: t.code, label: t.label }))}
          hint={
            (relTypes?.length ?? 0) === 0
              ? 'No relationship types configured yet. Add them in Settings.'
              : undefined
          }
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={addMember.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={addMember.isPending}>
            Add Member
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Create group (auto-adds the current contact) ───────────────────────────

function CreateGroupForContactModal({
  contactId,
  onClose,
}: {
  contactId: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [groupType, setGroupType] = useState('');
  const [error, setError] = useState('');
  const { data: groupTypes } = useGroupTypes();
  const create = useCreateContactGroup();
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Group name is required');
      return;
    }
    create.mutate(
      {
        name: trimmed,
        group_type: groupType || null,
        // Auto-add the current contact as the first member + primary/billing.
        member_ids: [contactId],
        primary_contact_id: contactId,
      } as ContactGroupSummary & { member_ids: string[] },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Group created' });
          onClose();
        },
        onError: () => setError('Could not create group'),
      },
    );
  };

  return (
    <Modal open onClose={onClose} title="Create Group" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error}
          placeholder="e.g., Smith Family Group"
          autoFocus
        />
        <Select
          label="Group type"
          value={groupType}
          onChange={(e) => setGroupType(e.target.value)}
          placeholder="Select a type (optional)"
          options={(groupTypes ?? []).map((t) => ({ value: t.code, label: t.label }))}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This contact will be added as the first member and billing contact.
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create Group
          </Button>
        </div>
      </form>
    </Modal>
  );
}
